import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit2, Check, X, Merge } from 'lucide-react';
import { useTranscriptStore } from '@/store';
import type { Speaker } from '@/types';

interface SpeakerEditorProps {
  speakers: Speaker[];
}

export function SpeakerEditor({ speakers }: SpeakerEditorProps) {
  const updateSpeaker = useTranscriptStore((state) => state.updateSpeaker);
  const mergeSpeakers = useTranscriptStore((state) => state.mergeSpeakers);
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSource, setMergeSource] = useState<string | null>(null);

  const predefinedColors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  ];

  const startEditing = (speaker: Speaker) => {
    setEditingId(speaker.id);
    setEditName(speaker.name);
    setEditColor(speaker.color);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('');
  };

  const saveEditing = () => {
    if (editingId && editName.trim()) {
      updateSpeaker(editingId, {
        name: editName.trim(),
        color: editColor,
      });
      cancelEditing();
    }
  };

  const handleMergeClick = (speakerId: string) => {
    if (!mergeMode) {
      setMergeMode(true);
      setMergeSource(speakerId);
    } else if (mergeSource && mergeSource !== speakerId) {
      // Merge source into target (speakerId)
      mergeSpeakers(mergeSource, speakerId);
      setMergeMode(false);
      setMergeSource(null);
    }
  };

  const cancelMerge = () => {
    setMergeMode(false);
    setMergeSource(null);
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Manage Speakers
        </h4>
        {mergeMode && (
          <button
            onClick={cancelMerge}
            className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Cancel merge
          </button>
        )}
      </div>

      {/* Merge hint */}
      {mergeMode && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/30 rounded-lg p-3"
        >
          <p className="text-sm text-purple-700 dark:text-purple-300">
            <strong>Merge mode:</strong> Select another speaker to merge "{speakers.find(s => s.id === mergeSource)?.name}" into.
          </p>
        </motion.div>
      )}

      {/* Speaker List */}
      <div className="space-y-2">
        <AnimatePresence>
          {speakers.map((speaker) => {
            const isEditing = editingId === speaker.id;
            const isSource = mergeSource === speaker.id;

            return (
              <motion.div
                key={speaker.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`p-3 rounded-xl border transition-all ${
                  isSource
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700'
                    : mergeMode && !isSource
                    ? 'bg-white dark:bg-slate-800 border-dashed border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/10 cursor-pointer'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                }`}
                onClick={() => mergeMode && !isSource && handleMergeClick(speaker.id)}
              >
                {isEditing ? (
                  <div className="space-y-3">
                    {/* Name input */}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none text-slate-900 dark:text-white"
                        placeholder="Speaker name"
                        autoFocus
                      />
                    </div>

                    {/* Color picker */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 dark:text-slate-400">Color:</span>
                      <div className="flex gap-1 flex-wrap">
                        {predefinedColors.map((color) => (
                          <button
                            key={color}
                            onClick={() => setEditColor(color)}
                            className={`w-6 h-6 rounded-full transition-transform ${
                              editColor === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={saveEditing}
                        disabled={!editName.trim()}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg hover:bg-emerald-200 dark:hover:bg-emerald-900/50 disabled:opacity-50"
                      >
                        <Check className="w-3 h-3" />
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600"
                      >
                        <X className="w-3 h-3" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md"
                        style={{ backgroundColor: speaker.color }}
                      >
                        {speaker.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900 dark:text-white">
                        {speaker.name}
                      </span>
                    </div>

                    {!mergeMode && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEditing(speaker)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          title="Edit speaker"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {speakers.length > 1 && (
                          <button
                            onClick={() => handleMergeClick(speaker.id)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                            title="Merge with another speaker"
                          >
                            <Merge className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}

                    {mergeMode && !isSource && (
                      <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                        Click to merge here →
                      </span>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
